import { Injectable } from '@angular/core';

import { Observable, Subscriber } from 'rxjs';

import { ApiConfiguration } from '../api/api-configuration';
import { ChatMessage } from '../api/models';

/**
 * An event of a chat message's stream: its SSE event name, and its data as
 * cctool-go's `AIChatHandler.SendMessage` (`internal/coolapi/ai_chat.go`)
 * writes it, which cool-api.yaml does not describe.
 */
export type ChatStreamEvent =
  | { type: 'message-start'; data: { session_id: string; model_id: string } }
  | { type: 'content-delta'; data: { text: string } }
  | { type: 'tool-call'; data: { tool: string; status: string } }
  | { type: 'message-end'; data: ChatMessage }
  | { type: 'error'; data: { message: string } };

/**
 * AI: AiChatSseService handles the streaming of AI chat messages via Server-Sent Events.
 * Since standard EventSource only supports GET, this service uses fetch with ReadableStream
 * to allow sending user messages via POST while receiving a stream of events.
 */
@Injectable({
  providedIn: 'root'
})
export class AiChatSseService {
  constructor(private apiConfig: ApiConfiguration) {}

  public sendMessageStream(invoiceId: number, sessionId: string, content: string): Observable<ChatStreamEvent> {
    return new Observable<ChatStreamEvent>(subscriber => {
      const url = `${this.apiConfig.rootUrl}/invoices/${invoiceId}/chat/sessions/${sessionId}/messages`;
      
      const controller = new AbortController();
      
      fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'text/event-stream'
        },
        body: JSON.stringify({ content }),
        signal: controller.signal
      }).then(response => {
        if (!response.ok) {
          subscriber.error(`HTTP error ${response.status}: ${response.statusText}`);
          return;
        }

        const reader = response.body?.getReader();
        const decoder = new TextDecoder();

        if (!reader) {
          subscriber.error('Response body is null');
          return;
        }

        const push = () => {
          reader.read().then(({ done, value }) => {
            if (done) {
              subscriber.complete();
              return;
            }

            const chunk = decoder.decode(value, { stream: true });
            this.parseChunks(chunk, subscriber);
            push();
          }).catch(err => subscriber.error(err));
        };

        push();
      }).catch(err => subscriber.error(err));

      return () => controller.abort();
    });
  }

  private parseChunks(chunk: string, subscriber: Subscriber<ChatStreamEvent>) {
    // AI: Simple SSE parser that handles multiple events in a single chunk
    const lines = chunk.split('\n');
    let currentEvent = '';
    
    for (const line of lines) {
      if (line.trim() === '') {
        continue;
      }

      if (line.startsWith('event: ')) {
        currentEvent = line.substring(7).trim();
      } else if (line.startsWith('data: ')) {
        const dataStr = line.substring(6).trim();
        let data: unknown;
        try {
          data = JSON.parse(dataStr);
        } catch {
          data = dataStr;
        }
        // The cast takes the server at its word: cool-api.yaml does not
        // describe this stream, so nothing checks an event against
        // ChatStreamEvent, and data that is no JSON goes through as its raw
        // text, whatever the event.
        subscriber.next({ type: currentEvent, data } as ChatStreamEvent);
        // Reset event for next data block
        currentEvent = '';
      }
    }
  }
}
